/**
 * @description User-facing auth error messages for sign-in and sign-up flows
 *
 * Maps backend AUTH_* codes and OAuth redirect error params to contextual
 * messages. Used by merchant frontend and admin portal sign-in/sign-up pages.
 *
 * @dependencies
 * - None
 *
 * @notes
 * - Backend messages are the fallback when no UI override exists
 * - OAuth URL errors (e.g. ?error=session_expired) are handled separately
 */

/** OAuth redirect / callback error codes surfaced on /sign-in */
const OAUTH_URL_ERROR_MESSAGES = {
  access_denied: 'Access was denied. Please try signing in again.',
  invalid_request: 'Your sign-in session is invalid. Please try again.',
  invalid_state: 'Your sign-in session expired. Please try again.',
  invalid_callback: 'Sign-in could not be completed. Please try again.',
  invalid_token: 'Your sign-in link expired. Please try again.',
  session_expired: 'Your session has expired. Please sign in again.',
  callback_failed: 'Sign-in failed. Please try again.',
  login_failed: 'Sign-in could not be started. Please try again.',
  login_required: 'Please sign in to continue.',
  consent_required: 'Additional permissions are required. Please try again.',
};

/** Backend AUTH_* code → sign-in message */
const SIGN_IN_ERROR_MESSAGES = {
  AUTH_INVALID_CREDENTIALS: 'Invalid email or password. Please try again.',
  AUTH_MISSING_CREDENTIALS: 'Please enter your email and password.',
  AUTH_MISSING_EMAIL: 'Please enter your email address.',
  AUTH_MISSING_PASSWORD: 'Please enter your password.',
  AUTH_INVALID_EMAIL: 'Please enter a valid email address.',
  AUTH_USER_LOCKED: 'Your account has been temporarily locked. Please contact support.',
  AUTH_USER_DISABLED: 'This account has been disabled. Please contact support.',
  AUTH_EMAIL_NOT_VERIFIED: 'Please verify your email address before signing in.',
  AUTH_PASSWORD_EXPIRED: 'Your password has expired. Please reset your password.',
  AUTH_PASSWORD_RESET_REQUIRED: 'You must reset your password before signing in.',
  AUTH_MFA_ENROLLMENT_REQUIRED: 'Multi-factor authentication setup is required for this account.',
  AUTH_MFA_CHALLENGE_REQUIRED: 'Multi-factor authentication verification is required.',
  AUTH_MISSING_REQUEST_TOKEN: 'Your sign-in session expired. Restarting sign-in…',
  MISSING_AUTH_REQUEST: 'Your sign-in session expired. Restarting sign-in…',
  AUTH_SESSION_EXPIRED: 'Your session has expired. Please sign in again.',
  AUTH_RATE_LIMITED: 'Too many sign-in attempts. Please wait a moment and try again.',
  AUTH_SERVICE_UNAVAILABLE: 'Sign-in is temporarily unavailable. Please try again shortly.',
  AUTH_TIMEOUT: 'Sign-in timed out. Please check your connection and try again.',
  AUTH_LOGIN_FAILED: 'Unable to sign in right now. Please try again.',
  AUTH_BAD_REQUEST: 'Please check your email and password and try again.',
  AUTH_VALIDATION_FAILED: 'Please check your email and password and try again.',
};

/** Backend AUTH_* code → sign-up message */
const SIGN_UP_ERROR_MESSAGES = {
  AUTH_EMAIL_EXISTS: 'An account with this email already exists. Please sign in instead.',
  AUTH_INVALID_EMAIL: 'Please enter a valid email address.',
  AUTH_MISSING_EMAIL: 'Email is required.',
  AUTH_MISSING_FIRST_NAME: 'First name is required.',
  AUTH_MISSING_LAST_NAME: 'Last name is required.',
  AUTH_NAME_TOO_LONG: 'Name must be 100 characters or fewer.',
  AUTH_MISSING_FIELDS: 'Please fill in all required fields.',
  AUTH_VALIDATION_FAILED: 'Please check your information and try again.',
  AUTH_WEAK_PASSWORD: 'Password does not meet security requirements.',
  AUTH_RATE_LIMITED: 'Too many attempts. Please wait a moment and try again.',
  AUTH_SERVICE_UNAVAILABLE: 'Registration is temporarily unavailable. Please try again shortly.',
  AUTH_TIMEOUT: 'Registration timed out. Please check your connection and try again.',
  AUTH_SIGNUP_FAILED: 'Unable to create your account right now. Please try again.',
  AUTH_SIGNUP_DISABLED: 'Public signup is not available. Sign in or ask for an invite.',
  AUTH_USER_DISABLED: 'This account cannot be created. Please contact support.',
};

/** Backend AUTH_* code → sign-up field name (for inline field errors) */
const SIGN_UP_FIELD_ERROR_MAP = {
  AUTH_MISSING_EMAIL: 'email',
  AUTH_INVALID_EMAIL: 'email',
  AUTH_MISSING_FIRST_NAME: 'firstName',
  AUTH_MISSING_LAST_NAME: 'lastName',
  AUTH_NAME_TOO_LONG: null,
  AUTH_VALIDATION_FAILED: null,
};

/**
 * Resolve OAuth URL error param to a user-facing message
 *
 * @param {string|null|undefined} urlError - Value of ?error= query param
 * @param {string} [fallback] - Optional fallback when code is unknown
 * @returns {string|null}
 */
export function resolveOAuthUrlError(urlError, fallback = 'Sign-in failed. Please try again.') {
  if (!urlError) {
    return null;
  }
  return OAUTH_URL_ERROR_MESSAGES[urlError] || fallback;
}

/**
 * Resolve sign-in API or client error to a user-facing message
 *
 * @param {object} params
 * @param {string} [params.errorCode] - Backend error.code
 * @param {string} [params.errorMessage] - Backend error.message
 * @param {number} [params.httpStatus] - HTTP status from axios response
 * @param {string} [params.portal] - 'merchant' | 'admin' for portal-specific copy
 * @returns {string}
 */
export function resolveSignInError({
  errorCode,
  errorMessage,
  httpStatus,
  portal = 'merchant',
}) {
  if (errorCode && SIGN_IN_ERROR_MESSAGES[errorCode]) {
    if (errorCode === 'AUTH_USER_LOCKED' && portal === 'admin') {
      return 'Account locked. Contact a platform administrator.';
    }
    return SIGN_IN_ERROR_MESSAGES[errorCode];
  }

  if (httpStatus === 429) {
    return SIGN_IN_ERROR_MESSAGES.AUTH_RATE_LIMITED;
  }

  if (errorMessage) {
    return errorMessage;
  }

  return 'Unable to sign in. Please check your connection and try again.';
}

/**
 * Resolve sign-up API error to general message and optional field error
 *
 * @param {object} params
 * @param {string} [params.errorCode] - Backend error.code
 * @param {string} [params.errorMessage] - Backend error.message
 * @param {string} [params.errorField] - Backend error.field
 * @param {number} [params.httpStatus] - HTTP status from axios response
 * @returns {{ generalMessage: string, fieldErrors: Record<string, string> }}
 */
export function resolveSignUpError({
  errorCode,
  errorMessage,
  errorField,
  httpStatus,
}) {
  const fieldErrors = {};

  if (errorCode && SIGN_UP_ERROR_MESSAGES[errorCode]) {
    const mappedField = errorField || SIGN_UP_FIELD_ERROR_MAP[errorCode];
    const message = SIGN_UP_ERROR_MESSAGES[errorCode];

    if (mappedField) {
      fieldErrors[mappedField] = message;
      return { generalMessage: null, fieldErrors };
    }

    return { generalMessage: message, fieldErrors };
  }

  if (httpStatus === 429) {
    return {
      generalMessage: SIGN_UP_ERROR_MESSAGES.AUTH_RATE_LIMITED,
      fieldErrors,
    };
  }

  if (errorMessage) {
    return { generalMessage: errorMessage, fieldErrors };
  }

  return {
    generalMessage: 'Unable to create account. Please check your connection and try again.',
    fieldErrors,
  };
}

/**
 * Codes that should restart the OAuth sign-in flow (clear token + redirect)
 *
 * @param {string} errorCode
 * @returns {boolean}
 */
export function shouldRestartSignInFlow(errorCode) {
  return errorCode === 'AUTH_MISSING_REQUEST_TOKEN' || errorCode === 'MISSING_AUTH_REQUEST';
}

export {
  OAUTH_URL_ERROR_MESSAGES,
  SIGN_IN_ERROR_MESSAGES,
  SIGN_UP_ERROR_MESSAGES,
  SIGN_UP_FIELD_ERROR_MAP,
};
